/**
 * Alumni dashboard — profile, assigned tasks, Odd/Even term, locked history
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import RoleDashboardShell from '../components/RoleDashboardShell';
import {
  alumniMenuItems,
  alumniMenuGroups,
  isValidAlumniMenuId,
} from '../config/roleDashboardMenus';

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending' },
  { id: 'completed', label: 'Completed' },
  { id: 'upcoming', label: 'Upcoming' },
];

const TASK_LABELS = {
  PROGRAM_STRUCTURE: 'Program structure',
  SUBJECT_SYLLABUS: 'Subject syllabus',
  INDUSTRY_EXPERT: 'Industry expert',
  SEMINAR: 'Seminar',
  EVENT: 'Event',
};

function openAlumniWindow(path) {
  const base = process.env.PUBLIC_URL || '';
  const url = `${window.location.origin}${base}${path}`;
  const popup = window.open(url, '_blank');
  if (popup) {
    popup.opener = null;
    return;
  }
  window.location.assign(url);
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function initials(name) {
  return (name || 'AL')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function Field({ label, value }) {
  return (
    <div className="profile-field">
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
}

function TaskCard({ task, onSubmit, onPreview }) {
  return (
    <article className={`stu-subject-card is-${task.status}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
            {TASK_LABELS[task.taskType] || task.taskType}
          </p>
          <h3 className="text-lg font-semibold m-0" style={{ color: 'var(--text-primary)' }}>{task.title}</h3>
        </div>
        <span className={`stu-chip stu-chip-${task.status}`}>{task.status}</span>
      </div>
      <p className="text-sm m-0" style={{ color: 'var(--text-secondary)' }}>
        {[task.cycleName, task.academicYear, task.subjectCode, task.eventName].filter(Boolean).join(' · ')}
      </p>
      {task.description ? <p className="text-sm m-0" style={{ color: 'var(--text-muted)' }}>{task.description}</p> : null}
      <p className="text-xs m-0" style={{ color: 'var(--text-muted)' }}>
        {formatDate(task.startAt)} – {formatDate(task.endAt)}
        {task.status === 'pending' && !task.submissionId ? ' · Action needed before the window closes' : ''}
        {task.status === 'completed' ? ' · Locked · cannot edit' : ''}
      </p>
      <div className="flex flex-wrap gap-2 mt-auto">
        {(task.status === 'active' || task.status === 'pending') && (
          <button type="button" className="btn-primary" disabled={!task.canSubmit} onClick={() => onSubmit(task)}>
            Submit feedback
          </button>
        )}
        {task.status === 'completed' && (
          <button type="button" className="btn-secondary" onClick={() => onPreview(task)}>Preview</button>
        )}
        {task.status === 'upcoming' && (
          <button type="button" className="btn-secondary" disabled>Opens with the next window</button>
        )}
      </div>
    </article>
  );
}

export default function AlumniDashboard() {
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [profileForm, setProfileForm] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');

  const fetchWorkspace = useCallback(async () => {
    try {
      const res = await api.get('/alumni-feedback/dashboard');
      const data = res.data?.data || null;
      setWorkspace(data);
      if (data?.profile) {
        setProfileForm({
          contactNumber: data.profile.contactNumber || '',
          address: data.profile.address || '',
          city: data.profile.city || '',
          qualification: data.profile.qualification || '',
          highestQualification: data.profile.highestQualification || '',
          companyName: data.profile.companyName || '',
          jobTitle: data.profile.jobTitle || '',
          industry: data.profile.industry || '',
          linkedinUrl: data.profile.linkedinUrl || '',
          profileSummary: data.profile.profileSummary || '',
        });
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load alumni dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkspace();
    const onFocus = () => fetchWorkspace();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchWorkspace]);

  const profile = workspace?.profile;
  const term = workspace?.term;
  const stats = workspace?.stats || { active: 0, pending: 0, completed: 0, upcoming: 0 };
  const displayName = profile?.firstName
    ? `${profile.firstName} ${profile.lastName || ''}`.trim()
    : (user?.alumni ? `${user.alumni.first_name} ${user.alumni.last_name}` : user?.email);
  const termLabel = term?.term_type === 'EVEN' ? 'Even' : term?.term_type === 'ODD' ? 'Odd' : null;

  const filteredTasks = useMemo(() => {
    const list = workspace?.tasks || [];
    return statusFilter === 'all' ? list : list.filter((task) => task.status === statusFilter);
  }, [workspace, statusFilter]);

  const handleSubmit = (task) => {
    if (!task.canSubmit) return;
    openAlumniWindow(`/dashboard/alumni/feedback?taskId=${task.id}`);
  };

  const handlePreview = (task) => {
    if (!task.submissionId) return;
    openAlumniWindow(`/dashboard/alumni/preview/${task.submissionId}`);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMessage('');
    setError('');
    try {
      const res = await api.patch('/alumni-feedback/profile', profileForm);
      setWorkspace(res.data?.data || workspace);
      setProfileMessage('Your alumni profile was saved.');
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  return (
    <RoleDashboardShell
      title="Alumni Dashboard"
      subtitle={`${displayName}${profile?.programTitle || profile?.degree ? ` · ${profile.programTitle || profile.degree}` : ''}${profile?.completionYear ? ` · ${profile.completionYear}` : ''}`}
      brandTitle="Alumni Console"
      brandSubtitle={term ? `${term.term_type} term · ${term.academic_year}` : 'Institute alumni desk'}
      menuItems={alumniMenuItems}
      menuGroups={alumniMenuGroups}
      isValidMenuId={isValidAlumniMenuId}
      loading={loading}
      error={error}
    >
      {({ activeMenu }) => {
        if (activeMenu === 'overview') {
          return (
            <div className="space-y-6">
              <div className="alu-hero">
                <div className="stu-hero-row">
                  <div className="stu-hero-main">
                    <span className="stu-avatar">{initials(displayName)}</span>
                    <div>
                      <p className="text-sm text-white/80 mb-1">Welcome back</p>
                      <h2 className="text-2xl font-semibold m-0">{displayName}</h2>
                      <p className="mt-1 text-white/85 text-sm">
                        {[profile?.alumniCode, profile?.department, profile?.programTitle || profile?.degree, profile?.batch || profile?.completionYear]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                  </div>
                  <div className="stu-hero-cycle">
                    <p className="text-xs uppercase tracking-wide text-white/70 m-0">Active semester window</p>
                    <p className="font-semibold m-0 mt-1">
                      {termLabel ? `${termLabel} semester` : 'Term not published'}
                      {term?.academic_year ? ` · ${term.academic_year}` : ''}
                    </p>
                    <p className="text-sm text-white/80 m-0 mt-1">Only tasks assigned by admin appear here.</p>
                  </div>
                </div>
              </div>

              <div className="stu-stat-grid">
                {['active', 'pending', 'completed', 'upcoming'].map((key) => (
                  <button key={key} type="button" className={`stu-stat stu-stat-${key} text-left`} onClick={() => setStatusFilter(key)}>
                    <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{key}</p>
                    <p className="stu-stat-value">{stats[key] || 0}</p>
                  </button>
                ))}
              </div>

              <div className="stu-filter-bar">
                {STATUS_FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setStatusFilter(filter.id)}
                    className={`stu-filter ${statusFilter === filter.id ? 'is-on' : ''}`}
                  >
                    {filter.label}
                    {filter.id !== 'all' ? ` · ${stats[filter.id] || 0}` : ` · ${workspace?.tasks?.length || 0}`}
                  </button>
                ))}
              </div>

              {filteredTasks.length === 0 ? (
                <div className="admin-empty">
                  <p className="admin-empty-title">No assigned feedback</p>
                  <p className="admin-empty-text">When admin assigns program, syllabus, industry, seminar, or event feedback, it will appear here.</p>
                </div>
              ) : (
                <div className="stu-subject-grid">
                  {filteredTasks.map((task) => (
                    <TaskCard key={task.id} task={task} onSubmit={handleSubmit} onPreview={handlePreview} />
                  ))}
                </div>
              )}
            </div>
          );
        }

        if (activeMenu === 'profile') {
          return (
            <div className="admin-card">
              <div className="admin-card-body">
                <div className="stu-identity">
                  <span className="stu-avatar-sm" style={{ width: '3.25rem', height: '3.25rem', fontSize: '1.1rem' }}>{initials(displayName)}</span>
                  <div>
                    <h2 className="admin-section-title mb-1">Alumni profile</h2>
                    <p className="admin-section-subtitle mb-0">Academic identity is read-only. You may update contact, qualification notes, and placement details.</p>
                  </div>
                </div>
                {!profile ? (
                  <div className="admin-empty">
                    <p className="admin-empty-title">Profile not linked</p>
                    <p className="admin-empty-text">Ask admin to attach an alumni master record to this login.</p>
                  </div>
                ) : (
                  <>
                    <dl className="profile-grid mb-8">
                      <Field label="Full name" value={displayName} />
                      <Field label="Alumni code" value={profile.alumniCode} />
                      <Field label="Email" value={profile.email || user?.email} />
                      <Field label="Department" value={profile.department} />
                      <Field label="Program" value={profile.programTitle || profile.degree} />
                      <Field label="Batch" value={profile.batch} />
                      <Field label="Graduation year" value={profile.graduationYear} />
                      <Field label="Completion year" value={profile.completionYear} />
                      <Field label="Result / class" value={profile.classObtained} />
                      <Field label="CGPA" value={profile.cgpa} />
                      <Field label="Placement type" value={profile.placementType} />
                      <Field label="Package (LPA)" value={profile.packageLpa} />
                    </dl>
                    {profileForm ? (
                      <form onSubmit={handleSaveProfile} className="space-y-4">
                        <h3 className="admin-section-title">Editable details</h3>
                        {profileMessage ? <p className="text-sm text-emerald-700">{profileMessage}</p> : null}
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className="input-label">Contact number</label>
                            <input className="input" value={profileForm.contactNumber} onChange={(e) => setProfileForm((f) => ({ ...f, contactNumber: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">City</label>
                            <input className="input" value={profileForm.city} onChange={(e) => setProfileForm((f) => ({ ...f, city: e.target.value }))} />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="input-label">Address</label>
                            <input className="input" value={profileForm.address} onChange={(e) => setProfileForm((f) => ({ ...f, address: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Qualification</label>
                            <input className="input" value={profileForm.qualification} onChange={(e) => setProfileForm((f) => ({ ...f, qualification: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Highest qualification</label>
                            <input className="input" value={profileForm.highestQualification} onChange={(e) => setProfileForm((f) => ({ ...f, highestQualification: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Company</label>
                            <input className="input" value={profileForm.companyName} onChange={(e) => setProfileForm((f) => ({ ...f, companyName: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Job title</label>
                            <input className="input" value={profileForm.jobTitle} onChange={(e) => setProfileForm((f) => ({ ...f, jobTitle: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Industry</label>
                            <input className="input" value={profileForm.industry} onChange={(e) => setProfileForm((f) => ({ ...f, industry: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">LinkedIn</label>
                            <input className="input" value={profileForm.linkedinUrl} onChange={(e) => setProfileForm((f) => ({ ...f, linkedinUrl: e.target.value }))} />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="input-label">Profile summary</label>
                            <textarea className="input min-h-[100px]" value={profileForm.profileSummary} onChange={(e) => setProfileForm((f) => ({ ...f, profileSummary: e.target.value }))} />
                          </div>
                        </div>
                        <button type="submit" className="btn-primary" disabled={profileSaving}>{profileSaving ? 'Saving…' : 'Save my profile'}</button>
                      </form>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          );
        }

        if (activeMenu === 'academics') {
          return (
            <div className="space-y-6">
              <div className="alu-hero">
                <p className="text-sm text-white/80 m-0">Program · Placement · Batch</p>
                <h2 className="text-2xl font-semibold m-0 mt-1">{profile?.programTitle || profile?.degree || 'Program'}</h2>
                <p className="mt-2 text-white/85 text-sm m-0">
                  {[profile?.department, profile?.batch, termLabel ? `${termLabel} term` : null, term?.academic_year]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <div className="admin-card">
                <div className="admin-card-body">
                  <h2 className="admin-section-title">Academic record</h2>
                  <dl className="profile-grid">
                    <Field label="Institute" value={user?.institute?.name} />
                    <Field label="Department" value={profile?.department} />
                    <Field label="Program" value={profile?.programTitle || profile?.degree} />
                    <Field label="Batch" value={profile?.batch} />
                    <Field label="Completion year" value={profile?.completionYear} />
                    <Field label="Result" value={profile?.classObtained} />
                    <Field label="CGPA" value={profile?.cgpa} />
                  </dl>
                </div>
              </div>
              <div className="admin-card">
                <div className="admin-card-body">
                  <h2 className="admin-section-title">Placement & company</h2>
                  <dl className="profile-grid">
                    <Field label="Company" value={profile?.companyName} />
                    <Field label="Job title" value={profile?.jobTitle} />
                    <Field label="Industry" value={profile?.industry} />
                    <Field label="Placement type" value={profile?.placementType} />
                    <Field label="Package (LPA)" value={profile?.packageLpa} />
                    <Field label="Contact" value={profile?.contactNumber} />
                  </dl>
                </div>
              </div>
              <div className="admin-card">
                <div className="admin-card-body">
                  <h2 className="admin-section-title">Active semester window</h2>
                  {term ? (
                    <dl className="profile-grid">
                      <Field label="Academic year" value={term.academic_year} />
                      <Field label="Term" value={term.term_type === 'EVEN' ? 'Even (2, 4, 6, 8)' : 'Odd (1, 3, 5, 7)'} />
                      <Field label="Starts" value={formatDate(term.start_at)} />
                      <Field label="Ends" value={formatDate(term.end_at)} />
                    </dl>
                  ) : (
                    <p style={{ color: 'var(--text-muted)' }}>No live odd/even academic window is published.</p>
                  )}
                </div>
              </div>
            </div>
          );
        }

        if (activeMenu === 'history') {
          const history = workspace?.history || [];
          return (
            <div className="admin-card">
              <div className="admin-card-body">
                <h2 className="admin-section-title">Submitted feedback history</h2>
                <p className="admin-section-subtitle">Read-only. Completed alumni feedback cannot be changed.</p>
                {history.length === 0 ? (
                  <div className="admin-empty">
                    <p className="admin-empty-title">No submissions yet</p>
                    <p className="admin-empty-text">Preview will appear here after you lock a task.</p>
                  </div>
                ) : (
                  <div className="stu-subject-grid">
                    {history.map((task) => (
                      <TaskCard key={task.id} task={task} onSubmit={handleSubmit} onPreview={handlePreview} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        }

        return null;
      }}
    </RoleDashboardShell>
  );
}
